<script>
/**
 * @component LiquidationSubmissionInvoiceTab
 * @description UC-REG-180/181/182 Fases 2/3/4 del ciclo de liquidación.
 *              Tab "Factura" del submission detail — permite cargar la
 *              factura del prestador, agregar notas de crédito para ajustar
 *              diferencias, y marcar pago con comprobante.
 * @example <LiquidationSubmissionInvoiceTab :submission-id="id" :accepted-amount="84000" @updated="fetchParent" />
 */
import submissionSvc from "@/services/liquidation-submission";

const emptyInvoiceForm = () => ({
  invoiceNumber: "",
  invoiceDate: "",
  cuit: "",
  razonSocial: "",
  netAmount: null,
  taxAmount: null,
  totalAmount: null,
});

const emptyNcForm = () => ({
  ncNumber: "",
  amount: null,
  ncDate: "",
  reason: "",
});

const emptyPaymentForm = () => ({
  paidAt: "",
  amount: null,
  paymentMethod: "",
  bankRef: "",
  notes: "",
});

export default {
  name: "LiquidationSubmissionInvoiceTab",
  props: {
    submissionId: { type: String, required: true },
    acceptedAmount: { type: Number, default: 0 },
    canEdit: { type: Boolean, default: true },
  },
  data() {
    return {
      invoice: null,
      loading: false,
      // Factura
      showInvoiceForm: false,
      invoiceForm: emptyInvoiceForm(),
      invoiceFile: null,
      savingInvoice: false,
      // NC
      showNcForm: false,
      ncForm: emptyNcForm(),
      ncFile: null,
      savingNc: false,
      // Pago
      showPaymentForm: false,
      paymentForm: emptyPaymentForm(),
      paymentFile: null,
      savingPayment: false,
    };
  },
  computed: {
    /** @returns {number} Total efectivo = factura - Σ NCs */
    effectiveTotal() {
      if (!this.invoice) return 0;
      const nc = (this.invoice.creditNotes || []).reduce(
        (s, x) => s + Number(x.amount || 0),
        0
      );
      return Number(this.invoice.totalAmount || 0) - nc;
    },
    /** @returns {number} Diferencia vs aceptado */
    diff() {
      return this.effectiveTotal - Number(this.acceptedAmount || 0);
    },
    matchLabel() {
      if (!this.invoice) return "Sin factura";
      if (this.invoice.matchStatus === "ok") return "Coincide";
      if (this.invoice.matchStatus === "mismatch") return "Diferencia";
      return "Pendiente";
    },
    matchClass() {
      if (!this.invoice) return "chip--neutral";
      if (this.invoice.matchStatus === "ok") return "chip--ok";
      if (this.invoice.matchStatus === "mismatch") return "chip--err";
      return "chip--warn";
    },
  },
  created() {
    this.fetchInvoice();
  },
  methods: {
    async fetchInvoice() {
      this.loading = true;
      try {
        const res = await submissionSvc.getInvoice(this.submissionId);
        this.invoice = res.invoice;
        if (!this.invoice) {
          // Sin factura → precargo el form con el monto esperado
          this.invoiceForm = {
            ...emptyInvoiceForm(),
            totalAmount: this.acceptedAmount,
            netAmount: this.acceptedAmount,
          };
          this.showInvoiceForm = true;
        } else {
          this.showInvoiceForm = false;
        }
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al cargar factura"
        );
      } finally {
        this.loading = false;
      }
    },
    editInvoice() {
      this.invoiceForm = {
        invoiceNumber: this.invoice.invoiceNumber || "",
        invoiceDate: this.invoice.invoiceDate
          ? this.invoice.invoiceDate.substr(0, 10)
          : "",
        cuit: this.invoice.cuit || "",
        razonSocial: this.invoice.razonSocial || "",
        netAmount: this.invoice.netAmount,
        taxAmount: this.invoice.taxAmount,
        totalAmount: this.invoice.totalAmount,
      };
      this.invoiceFile = null;
      this.showInvoiceForm = true;
    },
    async submitInvoice() {
      const f = this.invoiceForm;
      if (!f.invoiceNumber || !f.invoiceDate || f.totalAmount == null) {
        this.$message.warning("Nº, fecha y total son obligatorios");
        return;
      }
      this.savingInvoice = true;
      try {
        const res = await submissionSvc.saveInvoice(
          this.submissionId,
          {
            invoiceNumber: f.invoiceNumber,
            invoiceDate: f.invoiceDate,
            cuit: f.cuit,
            razonSocial: f.razonSocial,
            netAmount: f.netAmount,
            taxAmount: f.taxAmount,
            totalAmount: f.totalAmount,
          },
          this.invoiceFile
        );
        this.invoice = res.invoice;
        this.showInvoiceForm = false;
        this.invoiceFile = null;
        this.$message.success("Factura guardada");
        this.$emit("updated");
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al guardar factura"
        );
      } finally {
        this.savingInvoice = false;
      }
    },
    async deleteInvoice() {
      try {
        await this.$confirm(
          "¿Eliminar factura? Se pierden también las notas de crédito y el pago. Los ServiceRecord vuelven a Liquidada.",
          "Confirmar",
          {
            type: "warning",
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
          }
        );
      } catch {
        return;
      }
      try {
        await submissionSvc.deleteInvoice(this.submissionId);
        this.invoice = null;
        this.$message.success("Factura eliminada");
        this.$emit("updated");
        this.fetchInvoice();
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al eliminar factura"
        );
      }
    },
    async submitNc() {
      const n = this.ncForm;
      if (!n.ncNumber || n.amount == null) {
        this.$message.warning("Nº y monto son obligatorios");
        return;
      }
      this.savingNc = true;
      try {
        const res = await submissionSvc.addCreditNote(
          this.submissionId,
          n,
          this.ncFile
        );
        this.invoice = res.invoice;
        this.showNcForm = false;
        this.ncForm = emptyNcForm();
        this.ncFile = null;
        this.$message.success("Nota de crédito agregada");
        this.$emit("updated");
      } catch (err) {
        this.$message.error(err.response?.data?.error || "Error al agregar NC");
      } finally {
        this.savingNc = false;
      }
    },
    async deleteNc(ncId) {
      try {
        await this.$confirm("¿Eliminar esta nota de crédito?", "Confirmar", {
          type: "warning",
        });
      } catch {
        return;
      }
      try {
        const res = await submissionSvc.deleteCreditNote(
          this.submissionId,
          ncId
        );
        this.invoice = res.invoice;
        this.$message.success("Nota de crédito eliminada");
        this.$emit("updated");
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al eliminar NC"
        );
      }
    },
    openPaymentForm() {
      this.paymentForm = {
        ...emptyPaymentForm(),
        paidAt: new Date().toISOString().substr(0, 10),
        amount: this.effectiveTotal,
      };
      this.paymentFile = null;
      this.showPaymentForm = true;
    },
    async submitPayment() {
      const p = this.paymentForm;
      if (!p.paidAt || p.amount == null) {
        this.$message.warning("Fecha y monto son obligatorios");
        return;
      }
      this.savingPayment = true;
      try {
        const res = await submissionSvc.savePayment(
          this.submissionId,
          p,
          this.paymentFile
        );
        this.invoice = res.invoice;
        this.showPaymentForm = false;
        this.paymentFile = null;
        this.$message.success("Orden de pago enviada");
        this.$emit("updated");
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al enviar orden de pago"
        );
      } finally {
        this.savingPayment = false;
      }
    },
    async deletePayment() {
      try {
        await this.$confirm(
          "¿Cancelar la orden de pago? Los ServiceRecord vuelven a Facturada.",
          "Confirmar",
          { type: "warning" }
        );
      } catch {
        return;
      }
      try {
        await submissionSvc.deletePayment(this.submissionId);
        await this.fetchInvoice();
        this.$message.success("Orden de pago cancelada");
        this.$emit("updated");
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al cancelar orden de pago"
        );
      }
    },
    formatMoney(n) {
      if (n == null) return "-";
      return `$${Number(n).toLocaleString("es-AR", {
        minimumFractionDigits: 2,
      })}`;
    },
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("es-AR");
    },
    onInvoiceFile(file) {
      this.invoiceFile = file.raw;
      return false;
    },
    onNcFile(file) {
      this.ncFile = file.raw;
      return false;
    },
    onPaymentFile(file) {
      this.paymentFile = file.raw;
      return false;
    },
  },
};
</script>

<template lang="pug">
.inv-tab(v-loading="loading")
  //- ─────────── Sección Factura ───────────
  .inv-tab__section
    .inv-tab__header
      .inv-tab__title Factura del prestador
      span.chip(:class="matchClass") {{ matchLabel }}

    //- Vista de factura cargada
    .inv-tab__card(v-if="invoice && !showInvoiceForm")
      .inv-tab__grid
        .inv-tab__field
          span.lbl Nº factura
          span.val {{ invoice.invoiceNumber }}
        .inv-tab__field
          span.lbl Fecha
          span.val {{ formatDate(invoice.invoiceDate) }}
        .inv-tab__field
          span.lbl CUIT
          span.val {{ invoice.cuit || '—' }}
        .inv-tab__field
          span.lbl Razón social
          span.val {{ invoice.razonSocial || '—' }}
        .inv-tab__field
          span.lbl Neto
          span.val {{ formatMoney(invoice.netAmount) }}
        .inv-tab__field
          span.lbl IVA
          span.val {{ formatMoney(invoice.taxAmount) }}
        .inv-tab__field
          span.lbl Total factura
          span.val.strong {{ formatMoney(invoice.totalAmount) }}
        .inv-tab__field
          span.lbl NCs (Σ)
          span.val {{ formatMoney((invoice.creditNotes || []).reduce((s,x)=>s+Number(x.amount||0),0)) }}
        .inv-tab__field
          span.lbl Total efectivo
          span.val.strong {{ formatMoney(effectiveTotal) }}
        .inv-tab__field
          span.lbl Aceptado (submission)
          span.val {{ formatMoney(acceptedAmount) }}
        .inv-tab__field
          span.lbl Diferencia
          span.val(:class="{ 'amt--err': diff !== 0, 'amt--ok': diff === 0 }") {{ formatMoney(diff) }}
        .inv-tab__field(v-if="invoice.attachmentUrl")
          span.lbl PDF
          a.val(:href="invoice.attachmentUrl" target="_blank")
            i.el-icon-document
            |  Descargar
      .inv-tab__actions(v-if="canEdit")
        el-button(size="mini" @click="editInvoice") Editar
        el-button(size="mini" type="danger" plain @click="deleteInvoice") Eliminar

    //- Form de factura (crear o editar)
    .inv-tab__form(v-if="showInvoiceForm && canEdit")
      .inv-tab__grid
        .inv-tab__field
          label.lbl Nº factura *
          el-input(v-model="invoiceForm.invoiceNumber" size="small")
        .inv-tab__field
          label.lbl Fecha *
          el-input(v-model="invoiceForm.invoiceDate" type="date" size="small")
        .inv-tab__field
          label.lbl CUIT
          el-input(v-model="invoiceForm.cuit" size="small")
        .inv-tab__field
          label.lbl Razón social
          el-input(v-model="invoiceForm.razonSocial" size="small")
        .inv-tab__field
          label.lbl Neto
          el-input(v-model="invoiceForm.netAmount" type="number" size="small")
        .inv-tab__field
          label.lbl IVA
          el-input(v-model="invoiceForm.taxAmount" type="number" size="small")
        .inv-tab__field
          label.lbl Total factura *
          el-input(v-model="invoiceForm.totalAmount" type="number" size="small")
        .inv-tab__field
          label.lbl Adjuntar PDF
          el-upload(
            action=""
            :auto-upload="false"
            :on-change="onInvoiceFile"
            :show-file-list="true"
            :limit="1"
            accept=".pdf,image/*"
          )
            el-button(size="mini" icon="el-icon-upload") Seleccionar archivo
      .inv-tab__actions
        el-button(size="small" v-if="invoice" @click="showInvoiceForm = false") Cancelar
        el-button(
          type="primary" size="small"
          :loading="savingInvoice"
          @click="submitInvoice"
        ) Guardar

  //- ─────────── Sección Notas de crédito ───────────
  .inv-tab__section(v-if="invoice")
    .inv-tab__header
      .inv-tab__title Notas de crédito
      el-button(
        v-if="canEdit && !showNcForm"
        size="mini"
        type="primary"
        plain
        icon="el-icon-plus"
        @click="showNcForm = true"
      ) Agregar NC

    .inv-tab__empty(v-if="!showNcForm && (!invoice.creditNotes || !invoice.creditNotes.length)")
      | Sin notas de crédito.
      span(v-if="diff !== 0")  Diferencia con aceptado: {{ formatMoney(diff) }} — agregá una NC para ajustar.

    //- Lista de NCs
    table.inv-tab__nc-table(v-if="invoice.creditNotes && invoice.creditNotes.length")
      thead
        tr
          th Nº NC
          th Fecha
          th.amt Monto
          th Motivo
          th PDF
          th.acts(v-if="canEdit")
      tbody
        tr(v-for="nc in invoice.creditNotes" :key="nc._id")
          td {{ nc.ncNumber }}
          td {{ formatDate(nc.ncDate) }}
          td.amt {{ formatMoney(nc.amount) }}
          td.muted {{ nc.reason || '—' }}
          td
            a(v-if="nc.attachmentUrl" :href="nc.attachmentUrl" target="_blank")
              i.el-icon-document
              |  Ver
            span(v-else) —
          td.acts(v-if="canEdit")
            el-button(type="danger" plain size="mini" icon="el-icon-delete" @click="deleteNc(nc._id)")

    //- Form NC
    .inv-tab__form(v-if="showNcForm && canEdit")
      .inv-tab__grid
        .inv-tab__field
          label.lbl Nº NC *
          el-input(v-model="ncForm.ncNumber" size="small")
        .inv-tab__field
          label.lbl Monto *
          el-input(v-model="ncForm.amount" type="number" size="small")
        .inv-tab__field
          label.lbl Fecha
          el-input(v-model="ncForm.ncDate" type="date" size="small")
        .inv-tab__field
          label.lbl PDF
          el-upload(
            action=""
            :auto-upload="false"
            :on-change="onNcFile"
            :show-file-list="true"
            :limit="1"
            accept=".pdf,image/*"
          )
            el-button(size="mini" icon="el-icon-upload") Adjuntar
        .inv-tab__field.inv-tab__field--full
          label.lbl Motivo
          el-input(v-model="ncForm.reason" type="textarea" :rows="2" size="small")
      .inv-tab__actions
        el-button(size="small" @click="showNcForm = false; ncForm = { ncNumber:'', amount:null, ncDate:'', reason:'' }; ncFile = null") Cancelar
        el-button(
          type="primary" size="small"
          :loading="savingNc"
          @click="submitNc"
        ) Guardar NC

  //- ─────────── Sección Orden de pago (UC-REG-183) ───────────
  //- Semántica: acá se marca la ORDEN de pago enviada; el pago real
  //- vive fuera del sistema. La reconciliación llega en Fase 5 (import
  //- extractos bancarios).
  .inv-tab__section(v-if="invoice")
    .inv-tab__header
      .inv-tab__title Orden de pago
      span.chip.chip--sent(v-if="invoice.payment")
        i.el-icon-position
        |  Enviada a pagar

    //- Vista de orden de pago enviada
    .inv-tab__card(v-if="invoice.payment && !showPaymentForm")
      .inv-tab__grid
        .inv-tab__field
          span.lbl Fecha de orden
          span.val {{ formatDate(invoice.payment.paidAt) }}
        .inv-tab__field
          span.lbl Monto a pagar
          span.val.strong {{ formatMoney(invoice.payment.amount) }}
        .inv-tab__field
          span.lbl Método
          span.val {{ invoice.payment.paymentMethod || '—' }}
        .inv-tab__field
          span.lbl Ref. bancaria
          span.val {{ invoice.payment.bankRef || '—' }}
        .inv-tab__field.inv-tab__field--full(v-if="invoice.payment.notes")
          span.lbl Notas
          span.val {{ invoice.payment.notes }}
        .inv-tab__field(v-if="invoice.payment.attachmentUrl")
          span.lbl Comprobante interno
          a.val(:href="invoice.payment.attachmentUrl" target="_blank")
            i.el-icon-document
            |  Descargar
      .inv-tab__hint
        i.el-icon-info
        span  El pago efectivo se reconcilia contra extractos bancarios (próximamente).
      .inv-tab__actions(v-if="canEdit")
        el-button(size="mini" type="danger" plain @click="deletePayment") Cancelar orden de pago

    //- Botón para mandar a pagar
    .inv-tab__empty(v-if="!invoice.payment && !showPaymentForm")
      div(v-if="invoice.matchStatus === 'ok'")
        p.muted Factura y notas de crédito coinciden con lo aceptado. Podés mandar a pagar.
        el-button(
          v-if="canEdit"
          type="primary"
          size="small"
          icon="el-icon-position"
          @click="openPaymentForm"
        ) Mandar a pagar
      div(v-else)
        p.muted-err Ajustá la factura o agregá NC para que el total coincida con lo aceptado antes de mandar a pagar.

    //- Form orden de pago
    .inv-tab__form(v-if="showPaymentForm && canEdit")
      .inv-tab__grid
        .inv-tab__field
          label.lbl Fecha de orden *
          el-input(v-model="paymentForm.paidAt" type="date" size="small")
        .inv-tab__field
          label.lbl Monto a pagar *
          el-input(v-model="paymentForm.amount" type="number" size="small")
        .inv-tab__field
          label.lbl Método
          el-select(v-model="paymentForm.paymentMethod" size="small" style="width:100%")
            el-option(label="Transferencia" value="transferencia")
            el-option(label="Cheque" value="cheque")
            el-option(label="Efectivo" value="efectivo")
            el-option(label="Otro" value="otro")
        .inv-tab__field
          label.lbl Referencia bancaria
          el-input(v-model="paymentForm.bankRef" size="small")
        .inv-tab__field.inv-tab__field--full
          label.lbl Notas
          el-input(v-model="paymentForm.notes" type="textarea" :rows="2" size="small")
        .inv-tab__field
          label.lbl Comprobante interno (opcional)
          el-upload(
            action=""
            :auto-upload="false"
            :on-change="onPaymentFile"
            :show-file-list="true"
            :limit="1"
            accept=".pdf,image/*"
          )
            el-button(size="mini" icon="el-icon-upload") Adjuntar
      .inv-tab__actions
        el-button(size="small" @click="showPaymentForm = false") Cancelar
        el-button(
          type="primary" size="small"
          :loading="savingPayment"
          @click="submitPayment"
        ) Confirmar orden de pago
</template>

<style lang="scss" scoped>
.inv-tab {
  font-family: "Plus Jakarta Sans", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.inv-tab__section {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 18px;
  background: white;
}
.inv-tab__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.inv-tab__title {
  font-size: 14px;
  font-weight: 600;
  color: #002d5e;
}
.inv-tab__card {
  padding: 8px 0;
}
.inv-tab__form {
  padding: 8px 0;
}
.inv-tab__grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px 20px;
}
.inv-tab__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  .lbl {
    font-size: 11px;
    font-weight: 600;
    color: #6b7294;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .val {
    font-size: 13px;
    color: #1a1f36;
    &.strong {
      font-weight: 700;
      color: #002d5e;
    }
  }
  &--full {
    grid-column: 1 / -1;
  }
}
.inv-tab__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}
.inv-tab__empty {
  padding: 12px 0;
  font-size: 13px;
  color: #6b7294;
}
.inv-tab__nc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 12px;
  th {
    text-align: left;
    padding: 8px 10px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    font-weight: 500;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  th.amt {
    text-align: right;
  }
  th.acts {
    width: 50px;
  }
  td {
    padding: 8px 10px;
    border-bottom: 0.5px solid #f1f5f9;
  }
  td.amt {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  td.acts {
    text-align: center;
  }
  td.muted {
    color: #6b7294;
    font-size: 12px;
  }
  a {
    color: #002d5e;
    text-decoration: none;
    font-size: 12px;
    &:hover {
      text-decoration: underline;
    }
  }
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
  &.chip--ok {
    background: #ecfdf5;
    color: #065f46;
    border-color: #a7f3d0;
  }
  &.chip--err {
    background: #fef2f2;
    color: #991b1b;
    border-color: #fecaca;
  }
  &.chip--warn {
    background: #fef3c7;
    color: #92400e;
    border-color: #fde68a;
  }
  &.chip--neutral {
    background: #f1f5f9;
    color: #475569;
    border-color: #cbd5e1;
  }
  &.chip--sent {
    background: #ede9fe;
    color: #5b21b6;
    border-color: #c4b5fd;
  }
  &.chip--paid {
    background: #d1fae5;
    color: #065f46;
    border-color: #6ee7b7;
  }
}
/* UC-REG-183 — hint bajo la orden de pago aclarando que la reconciliación
   real es en Fase 5. */
.inv-tab__hint {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  color: #6b7294;
  display: flex;
  align-items: center;
  gap: 6px;
  i {
    color: #94a3b8;
  }
}
.amt--ok {
  color: #059669;
  font-weight: 700;
}
.amt--err {
  color: #dc2626;
  font-weight: 700;
}
.muted {
  color: #6b7294;
  font-size: 13px;
  margin: 0 0 8px 0;
}
.muted-err {
  color: #92400e;
  font-size: 13px;
  margin: 0 0 8px 0;
}
</style>
